/**
 * 🩹 PATCH-350 (2026-02) · Privacy Policy
 * ---------------------------------------
 * Public disclosure of how The Learning Institute for Empowerment,
 * LLC (DBA LIFE LLC), operating The Exchange Alliance platform,
 * collects, uses, and safeguards Personally Identifiable Information
 * (PII), Protected Health Information (PHI), and participant
 * records. Structured to satisfy:
 *   · HIPAA/HITECH Privacy Rule (45 CFR Part 164, Subpart E)
 *   · CCPA / CPRA disclosure obligations for CA residents
 *   · GDPR Article 13/14 baseline disclosures
 *   · SC / NC state consumer notice statutes
 */
import LegalPageShell from "../components/LegalPageShell";

export default function PrivacyPolicy() {
  return (
    <LegalPageShell
      slug="privacy"
      eyebrow="Legal · Privacy"
      title="Privacy Policy"
      subtitle="How we collect, use, and safeguard the information entrusted to The Exchange Alliance platform."
      lastUpdated="2026-02-25"
    >
      <div className="callout">
        <strong>Operator of Record</strong>
        The Learning Institute for Empowerment, LLC (DBA <em>LIFE LLC</em>) operates
        this platform on behalf of The Exchange Alliance, a non-profit
        collaboration chartered by The Life Foundation and administered
        alongside credentialed partner agencies. Questions and privacy requests should reach us at{" "}
        <a href="mailto:privacy@theexchangealliance.org">privacy@theexchangealliance.org</a>.
      </div>

      <h2>1. Scope of this Policy</h2>
      <p>
        This Privacy Policy governs all data collected through the
        <strong> theexchangealliance.com </strong>marketing site, the Personal Advancement and Career Training
        System (PACT), the Resident Portal, Vendor Portal, Landlord Portal, Employer
        Portal, and any related sub-domain, mobile web experience, or partner
        integration surface operated by LIFE LLC (collectively, the "Platform").
      </p>
      <p>
        The Platform serves multiple distinct user classes: <strong>residents</strong>
        (program participants), <strong>staff</strong> (case managers, super admins,
        and executive directors), <strong>vendors</strong> (landlords, employers,
        service providers), and <strong>the public</strong> (donors, community
        members, prospective partners). The categories of information we collect —
        and the safeguards we apply — vary by user class.
      </p>

      <h2>2. Information We Collect</h2>

      <h3>2.1 Public Visitors</h3>
      <ul>
        <li>Newsletter subscription: email address only.</li>
        <li>Inquiry / contact forms: name, email, organization, message body.</li>
        <li>Donations: name, email, mailing address, payment token (never card numbers directly — see § 4).</li>
        <li>Usage telemetry: IP address, user-agent, referring URL, timestamps (for security + traffic analytics).</li>
      </ul>

      <h3>2.2 Program Participants (Residents)</h3>
      <ul>
        <li>Identity: legal name, date of birth, government ID references.</li>
        <li>Contact: phone, email, emergency contact.</li>
        <li>Program records: intake assessments, housing placements, employment history, financial-literacy paychecks, compliance milestones, milestone signatures.</li>
        <li>
          Sensitive categories (handled under § 5 HIPAA safeguards): behavioral
          health status, substance-use history, physical health notes, disability
          disclosures, faith affiliation (voluntary), survivor / trafficking history
          (voluntary; documented consent required).
        </li>
      </ul>

      <h3>2.3 Vendors, Landlords, Employers</h3>
      <ul>
        <li>Business identity: legal entity, EIN, remittance details, W-9 records.</li>
        <li>Invoices and payment mirrors — no consumer PHI is routed to vendors.</li>
      </ul>

      <h3>2.4 Staff</h3>
      <ul>
        <li>Employment identity, role assignment, MFA credentials, session telemetry, audit-log actor stamps.</li>
      </ul>

      <h2>3. How We Use Information</h2>
      <p>
        We use information only for purposes disclosed at the point of collection
        and for the ongoing operation of the program. Explicitly:
      </p>
      <ul>
        <li><strong>Service delivery</strong> — case management, housing placement, paycheck absorption, milestone tracking.</li>
        <li><strong>Care coordination</strong> — sharing minimum-necessary data with The Life Foundation and credentialed partner agencies under governance agreements.</li>
        <li><strong>Compliance</strong> — HIPAA, HMIS, funder audits, state licensing.</li>
        <li><strong>Communication</strong> — program updates, appointment reminders, opt-in newsletter.</li>
        <li><strong>Security</strong> — fraud prevention, incident response, audit-log integrity.</li>
      </ul>
      <p>
        We do <strong>not</strong> sell personal information. We do not use
        participant data for behavioral advertising, machine-learning training with
        third parties, or profiling outside the direct scope of the care model.
      </p>

      <h2>4. Payment Data</h2>
      <p>
        Payment card details are handled directly by our PCI-DSS Level 1 processor
        (Stripe, Inc.). LIFE LLC receives only tokenized references and last-four
        digits for reconciliation. No cardholder data is stored on the Platform.
      </p>

      <h2>5. Protected Health Information (PHI) — HIPAA Safeguards</h2>
      <p>
        When the Platform receives, creates, or transmits PHI on behalf of a covered
        entity or through the Alliance program, we apply the technical, physical,
        and administrative safeguards required by the HIPAA Security Rule
        (45 CFR § 164.308–316). See our{" "}
        <a href="/legal/hipaa">HIPAA Notice of Privacy Practices</a> and{" "}
        <a href="/legal/security">Security & Compliance</a> disclosures for the
        specific controls.
      </p>
      <p>
        Covered entities working with LIFE LLC may execute a{" "}
        <a href="/legal/baa">Business Associate Agreement (BAA)</a> before any PHI
        is exchanged. No PHI is transmitted absent an executed BAA.
      </p>

      <h2>6. Data Sharing</h2>
      <p>
        We share information only in the following, tightly scoped scenarios:
      </p>
      <table>
        <thead>
          <tr>
            <th>Recipient</th>
            <th>What</th>
            <th>Legal basis</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>The Life Foundation + credentialed partner agencies</td>
            <td>Minimum-necessary case data</td>
            <td>Governance charter + participant intake consent</td>
          </tr>
          <tr>
            <td>Goodwill Industries (C2C Initiative)</td>
            <td>Employment referral, placement outcomes</td>
            <td>Signed MOU (2026-08-16 → 2027-08-15) + participant consent</td>
          </tr>
          <tr>
            <td>CharityTracker / HMIS</td>
            <td>Housing status, service history</td>
            <td>Partner agreement + participant HMIS consent</td>
          </tr>
          <tr>
            <td>Government funders / auditors</td>
            <td>De-identified outcomes, program-level financials</td>
            <td>Grant agreements, state licensure</td>
          </tr>
          <tr>
            <td>Sub-processors (hosting, email, SMS)</td>
            <td>Encrypted operational data</td>
            <td>Vendor contracts with BAA where PHI is involved</td>
          </tr>
        </tbody>
      </table>

      <h2>7. Your Rights</h2>
      <p>
        Regardless of jurisdiction, every individual whose data we hold has the
        right to:
      </p>
      <ul>
        <li><strong>Access</strong> — request a copy of your records.</li>
        <li><strong>Correction</strong> — request that inaccurate data be corrected.</li>
        <li><strong>Deletion</strong> — request erasure, subject to legal-retention obligations (HMIS + grant compliance).</li>
        <li><strong>Restriction / Objection</strong> — limit certain processing, including opt-out of non-essential communications.</li>
        <li><strong>Portability</strong> — receive your data in a structured, machine-readable format.</li>
        <li><strong>Complaint</strong> — file a complaint with the U.S. Department of Health and Human Services (Office for Civil Rights) or your state Attorney General without retaliation.</li>
      </ul>
      <p>
        Send all rights requests to{" "}
        <a href="mailto:privacy@theexchangealliance.org">
          privacy@theexchangealliance.org
        </a>{" "}
        or the mailing address in § 12. We respond within 30 days.
      </p>

      <h2>8. Retention</h2>
      <p>
        Program participant records are retained for the duration of enrollment plus
        seven (7) years to satisfy HIPAA (§ 164.316(b)(2)(i)), HMIS, and federal
        grant audit requirements. Public-visitor telemetry is retained 90 days.
        Newsletter subscriptions persist until you unsubscribe.
      </p>

      <h2>9. Cookies & Tracking</h2>
      <p>
        We use strictly necessary cookies for authentication, session integrity,
        and CSRF defense. Analytics cookies (aggregate, non-identifiable) are opt-in
        through the cookie banner. We do <strong>not</strong> use advertising
        cookies or cross-site trackers.
      </p>

      <h2>10. Children's Privacy</h2>
      <p>
        The Platform is not directed to children under 13. Program participants
        under 18 are enrolled only with documented guardian consent and are
        subject to additional care protocols governed by state child-welfare law.
      </p>

      <h2>11. Changes to this Policy</h2>
      <p>
        Material changes to this Policy will be posted here with an updated "Last
        updated" date. For substantial changes affecting how we handle PHI, we
        will notify affected participants and covered entities in writing
        thirty (30) days before the change takes effect.
      </p>

      <h2>12. Contact</h2>
      <p><strong>The Learning Institute for Empowerment, LLC</strong> (DBA LIFE LLC)</p>
      <p>Operating The Exchange Alliance Platform</p>
      <ul>
        <li>Privacy Officer: <a href="mailto:privacy@theexchangealliance.org">privacy@theexchangealliance.org</a></li>
        <li>Compliance Officer: <a href="mailto:compliance@theexchangealliance.org">compliance@theexchangealliance.org</a></li>
        <li>Executive Director: William Smith · <a href="mailto:william@theexchangealliance.org">william@theexchangealliance.org</a></li>
        <li>Mailing Address: 7433 Springside Drive, Myrtle Beach, SC 29588</li>
        <li>EIN: 43-2209994</li>
      </ul>
    </LegalPageShell>
  );
}
